'use client'

import { useEffect, useRef } from 'react'
import { AlertTriangle, CheckCircle, Info, XCircle } from 'lucide-react'

import type { Severity } from '@/lib/context-response'

const STYLE: Record<
  Severity,
  { icon: typeof CheckCircle; bg: string; border: string; text: string }
> = {
  success: {
    icon: CheckCircle,
    bg: 'bg-green-50',
    border: 'border-green-200',
    text: 'text-green-800',
  },
  info: {
    icon: Info,
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-800',
  },
  warning: {
    icon: AlertTriangle,
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-800',
  },
  danger: {
    icon: XCircle,
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-800',
  },
}

const SIZE = {
  sm: {
    box: 'gap-2.5 px-4 py-3',
    icon: 'mt-0.5 size-4',
    text: 'text-sm',
  },
  lg: {
    box: 'gap-4 px-5 py-5 shadow-[4px_4px_0px_rgba(0,0,0,0.35)] sm:px-6 sm:py-6',
    icon: 'size-8 sm:size-10',
    text: 'font-heading text-2xl leading-tight font-semibold sm:text-3xl',
  },
}

interface ContextResponseProps {
  severity: Severity
  message: string
  size?: keyof typeof SIZE
  className?: string
}

export function ContextResponse({
  severity,
  message,
  size = 'sm',
  className = '',
}: ContextResponseProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const node = ref.current
    if (!node) return
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    node.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' })
    // Scrolling alone can still leave the reader looking elsewhere, and a
    // keyboard or screen reader user sitting on the send button with no idea
    // anything happened. Taking focus puts the answer under the cursor and
    // makes the next Tab continue from here. preventScroll so it does not
    // fight the pan that is already running.
    node.focus({ preventScroll: true })
  }, [message])

  const style = STYLE[severity]
  const scale = SIZE[size]
  const Icon = style.icon

  return (
    <div
      ref={ref}
      role="alert"
      aria-live="polite"
      tabIndex={-1}
      className={`flex items-start rounded-sm border ${style.bg} ${style.border} ${style.text} ${scale.box} ${className}`}
    >
      <Icon className={`shrink-0 ${scale.icon}`} aria-hidden="true" />
      <p className={scale.text}>{message}</p>
    </div>
  )
}
